"use client"

import type React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
    Heart,
    Coins,
    Star,
    Activity,
    Search,
    Sparkles,
    BookOpen,
    Wand2,
    Calendar,
    Zap,
    Lock,
} from "lucide-react"
import onboarding1 from "@/assets/images/onboarding/onboarding-1.png"
import onboarding2 from "@/assets/images/onboarding/onboarding-2.png"
import backCelestial from "@/assets/images/onboarding/back-celestial.jpg"
import backRiderWaite from "@/assets/images/onboarding/back-rider-waite.jpg"
import backThoth from "@/assets/images/onboarding/back-thoth.png"
import { AccountStep } from "./onboarding-account-creation"
import {
    EXPERIENCE_LEVELS_EN,
    READING_FREQUENCIES_EN,
    USER_GOALS_EN,
    AI_TONE_PREFERENCES_EN,
    DECK_PREFERENCES_EN,
} from "@/constants/profileOptions"
import { useTranslation } from 'react-i18next'

const SkipButton = ({
    onClick,
    children,
}: {
    onClick: () => void
    children: React.ReactNode
}) => (
    <Button
        onClick={onClick}
        variant="outline"
        className="bg-transparent text-[#3d405b] border-[#3d405b] hover:bg-[#3d405b] hover:text-white w-full text-base sm:text-lg font-serif py-2 sm:py-2.5"
    >
        {children}
    </Button>
)

// Common button component for all steps
const StepButton = ({
    onClick,
    children,
    variant = "default",
    disabled = false,
    className = "",
}: {
    onClick: () => void
    children: React.ReactNode
    variant?: "default" | "outline" | "secondary"
    disabled?: boolean
    className?: string
}) => (
    <Button
        onClick={onClick}
        disabled={disabled}
        className={`
            w-full py-2 sm:py-2.5 text-base sm:text-lg font-serif max-w-[600px] mx-auto text-white
            ${variant === "default" ? "bg-gradient-to-br from-[#2B2E4A] to-[#3D4066] hover:from-[#3D4066] hover:to-[#2B2E4A]" : ""}
            ${className}
        `}
        variant={variant}
    >
        {children}
    </Button>
)

export { AccountStep };

// User Goal Step
export function UserGoalStep({
    value,
    onChange,
    onNext,
}: {
    value: string
    onChange: (value: string) => void
    onNext: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-3 sm:space-y-4 overflow-y-auto">
                <img
                    src={onboarding1}
                    alt="Onboarding 1"
                    className="w-full h-auto max-h-[180px] sm:max-h-[240px] object-contain"
                />
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.userGoal.title')}</h2>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <GoalOption
                        icon={<Heart className="h-5 w-5 sm:h-6 sm:w-6 text-rose-500" />}
                        label={t('onboarding.userGoal.options.loveRelationships')}
                        selected={value === USER_GOALS_EN.LOVE_RELATIONSHIPS}
                        onClick={() => onChange(USER_GOALS_EN.LOVE_RELATIONSHIPS)}
                    />
                    <GoalOption
                        icon={<Coins className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />}
                        label={t('onboarding.userGoal.options.careerFinances')}
                        selected={value === USER_GOALS_EN.CAREER_FINANCES}
                        onClick={() => onChange(USER_GOALS_EN.CAREER_FINANCES)}
                    />
                    <GoalOption
                        icon={<Star className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.userGoal.options.personalGrowth')}
                        selected={value === USER_GOALS_EN.PERSONAL_GROWTH}
                        onClick={() => onChange(USER_GOALS_EN.PERSONAL_GROWTH)}
                    />
                    <GoalOption
                        icon={<Activity className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-500" />}
                        label={t('onboarding.userGoal.options.healthWellbeing')}
                        selected={value === USER_GOALS_EN.HEALTH_WELLBEING}
                        onClick={() => onChange(USER_GOALS_EN.HEALTH_WELLBEING)}
                    />
                    <GoalOption
                        icon={<Search className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />}
                        label={t('onboarding.userGoal.options.curiosityExploration')}
                        selected={value === USER_GOALS_EN.CURIOSITY_EXPLORATION}
                        onClick={() => onChange(USER_GOALS_EN.CURIOSITY_EXPLORATION)}
                        className="col-span-2"
                    />
                </div>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>
            </div>
        </div>
    )
}

function GoalOption({
    icon,
    label,
    selected,
    onClick,
    className = "",
}: {
    icon: React.ReactNode
    label: string
    selected: boolean
    onClick: () => void
    className?: string
}) {
    return (
        <div
            className={`${className} flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 ${selected
                ? "border-theme-dark bg-[#E8E9EF]"
                : "border-gray-200 hover:border-theme-dark hover:bg-[#F0F1F5]"
                }`}
            onClick={onClick}
        >
            <div className="mb-0.5 sm:mb-1">{icon}</div>
            <span className="text-center text-xs sm:text-sm font-medium text-theme-dark leading-tight">{label}</span>
        </div>
    )
}


export function TarotExperienceStep({
    value,
    onChange,
    onNext,
}: {
    value: string
    onChange: (value: string) => void
    onNext: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-3 sm:space-y-4 overflow-y-auto">
                <img
                    src={onboarding2}
                    alt="Onboarding 2"
                    className="w-full h-auto max-h-[180px] sm:max-h-[240px] object-contain"
                />
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.tarotExperience.title')}</h2>

                <div className="grid grid-cols-1 gap-2 sm:gap-3">
                    <ExperienceOption
                        icon={<Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.tarotExperience.options.newToTarot')}
                        description={t('onboarding.tarotExperience.options.newToTarotDescription')}
                        selected={value === EXPERIENCE_LEVELS_EN.NEW_TO_TAROT}
                        onClick={() => onChange(EXPERIENCE_LEVELS_EN.NEW_TO_TAROT)}
                    />
                    <ExperienceOption
                        icon={<BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.tarotExperience.options.someExperience')}
                        description={t('onboarding.tarotExperience.options.someExperienceDescription')}
                        selected={value === EXPERIENCE_LEVELS_EN.SOME_EXPERIENCE}
                        onClick={() => onChange(EXPERIENCE_LEVELS_EN.SOME_EXPERIENCE)}
                    />
                    <ExperienceOption
                        icon={<Wand2 className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.tarotExperience.options.experiencedReader')}
                        description={t('onboarding.tarotExperience.options.experiencedReaderDescription')}
                        selected={value === EXPERIENCE_LEVELS_EN.EXPERIENCED_READER}
                        onClick={() => onChange(EXPERIENCE_LEVELS_EN.EXPERIENCED_READER)}
                    />
                </div>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>
            </div>
        </div>
    )
}

function ExperienceOption({
    icon,
    label,
    description,
    selected,
    onClick,
}: {
    icon: React.ReactNode
    label: string
    description: string
    selected: boolean
    onClick: () => void
}) {
    return (
        <div
            className={`flex items-center p-2.5 sm:p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 h-auto max-h-[70px] ${selected
                ? "border-theme-dark bg-[#E8E9EF]"
                : "border-gray-200 hover:border-theme-dark hover:bg-[#F0F1F5]"
                }`}
            onClick={onClick}
        >
            <div className="mr-2 sm:mr-3 flex-shrink-0">{icon}</div>
            <div>
                <h3 className="font-medium text-base sm:text-lg text-theme-dark">{label}</h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-tight mb-0">{description}</p>
            </div>

        </div>
    )
}

// Deck Preference Step
export function DeckPreferenceStep({
    value,
    onChange,
    onNext,
    onSkip,
}: {
    value: string
    onChange: (value: string) => void
    onNext: () => void
    onSkip: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.deckPreference.title')}</h2>

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <DeckOption
                        name={t('onboarding.deckPreference.options.riderWaite')}
                        imageSrc={backRiderWaite}
                        selected={value === DECK_PREFERENCES_EN.RIDER_WAITE}
                        onClick={() => onChange(DECK_PREFERENCES_EN.RIDER_WAITE)}
                    />
                    <DeckOption
                        name={t('onboarding.deckPreference.options.celestialMinimalist')}
                        imageSrc={backCelestial}
                        selected={value === DECK_PREFERENCES_EN.CELESTIAL_MINIMALIST}
                        onClick={() => onChange(DECK_PREFERENCES_EN.CELESTIAL_MINIMALIST)}
                    />
                    <DeckOption
                        name={t('onboarding.deckPreference.options.thoth')}
                        imageSrc={backThoth}
                        selected={value === DECK_PREFERENCES_EN.THOTH}
                        onClick={() => onChange(DECK_PREFERENCES_EN.THOTH)}
                    />
                </div>
            </div>

            <div className="mt-auto pt-4 flex flex-col gap-3 sm:gap-4">
                <StepButton onClick={onNext} disabled={!value} className="bg-[#3d405b] text-white">
                    {t('onboarding.common.continue')}
                </StepButton>
                <SkipButton onClick={onSkip}>
                    {t('onboarding.common.skipForNow')}
                </SkipButton>
            </div>
        </div>
    )
}

function DeckOption({
    name,
    imageSrc,
    selected,
    onClick,
}: {
    name: string
    imageSrc: string
    selected: boolean
    onClick: () => void
}) {
    return (
        <div
            className={`flex flex-col items-center p-2 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${selected
                ? "border-theme-dark bg-[#E8E9EF]"
                : "border-gray-200 hover:border-theme-dark hover:bg-[#F0F1F5]"
                }`}
            onClick={onClick}
        >
            <img src={imageSrc || "/placeholder.svg"} alt={`${name} deck`} className="h-24 sm:h-32 object-contain mb-1 sm:mb-2" />
            <span className="text-center text-xs sm:text-sm font-medium text-theme-dark leading-tight">{name}</span>
        </div>
    )
}

// Birth Data Step
export function BirthDataStep({
    value,
    onChange,
    onNext,
    onSkip,
}: {
    value: {
        birthdate: string
        birth_time: string
        birth_place: string
    }
    onChange: (value: any) => void
    onNext: () => void
    onSkip: () => void
}) {
    const { t } = useTranslation();
    const handleChange = (field: string, fieldValue: string) => {
        onChange({
            ...value,
            [field]: fieldValue,
        })
    }

    const isComplete = value.birthdate || value.birth_time || value.birth_place

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.birthData.title')}</h2>

                <p className="text-center text-sm sm:text-base text-gray-500 px-2">{t('onboarding.birthData.helpAlign')}</p>

                <div className="space-y-3 sm:space-y-4">
                    <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="birthdate" className="text-sm sm:text-base">{t('onboarding.birthData.birthDate')}</Label>
                        <Input
                            id="birthdate"
                            type="date"
                            value={value.birthdate}
                            onChange={(e) => handleChange("birthdate", e.target.value)}
                            className="text-sm sm:text-base"
                        />
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="birth_time" className="text-sm sm:text-base">{t('onboarding.birthData.birthTime')}</Label>
                        <Input
                            id="birth_time"
                            type="time"
                            value={value.birth_time}
                            onChange={(e) => handleChange("birth_time", e.target.value)}
                            className="text-sm sm:text-base"
                        />
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="birth_place" className="text-sm sm:text-base">{t('onboarding.birthData.birthPlace')}</Label>
                        <Input
                            id="birth_place"
                            type="text"
                            placeholder={t('onboarding.birthData.cityCountryPlaceholder')}
                            value={value.birth_place}
                            onChange={(e) => handleChange("birth_place", e.target.value)}
                            className="text-sm sm:text-base"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-auto pt-4 flex flex-col gap-3 sm:gap-4">
                <StepButton onClick={onNext} disabled={!isComplete}>
                    {t('onboarding.common.continue')}
                </StepButton>

                <SkipButton onClick={onSkip}>
                    {t('onboarding.common.skipForNow')}
                </SkipButton>
            </div>
        </div>
    )
}

// Reading Frequency Step
export function ReadingFrequencyStep({
    value,
    onChange,
    onNext,
}: {
    value: string
    onChange: (value: string) => void
    onNext: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.readingFrequency.title')}</h2>

                <div className="flex flex-col space-y-2 sm:space-y-4">
                    <FrequencyOption
                        icon={<Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.readingFrequency.options.daily')}
                        description={t('onboarding.readingFrequency.options.dailyDescription')}
                        selected={value === READING_FREQUENCIES_EN.DAILY}
                        onClick={() => onChange(READING_FREQUENCIES_EN.DAILY)}
                    />
                    <FrequencyOption
                        icon={<Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.readingFrequency.options.weekly')}
                        description={t('onboarding.readingFrequency.options.weeklyDescription')}
                        selected={value === READING_FREQUENCIES_EN.WEEKLY}
                        onClick={() => onChange(READING_FREQUENCIES_EN.WEEKLY)}
                    />
                    <FrequencyOption
                        icon={<Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.readingFrequency.options.occasionally')}
                        description={t('onboarding.readingFrequency.options.occasionallyDescription')}
                        selected={value === READING_FREQUENCIES_EN.OCCASIONALLY}
                        onClick={() => onChange(READING_FREQUENCIES_EN.OCCASIONALLY)}
                    />
                </div>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>
            </div>
        </div>
    )
}

function FrequencyOption({
    icon,
    label,
    description,
    selected,
    onClick,
}: {
    icon: React.ReactNode
    label: string
    description: string
    selected: boolean
    onClick: () => void
}) {
    return (
        <div
            className={`flex items-center p-2.5 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${selected
                ? "border-theme-dark bg-[#E8E9EF]"
                : "border-gray-200 hover:border-theme-dark hover:bg-[#F0F1F5]"
                }`}
            onClick={onClick}
        >
            <div className="mr-2 sm:mr-4 flex-shrink-0">{icon}</div>
            <div>
                <h3 className="font-medium text-base sm:text-lg text-theme-dark">{label}</h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-tight mb-0">{description}</p>
            </div>
        </div>
    )
}

// AI Tone Step
export function AIToneStep({
    value,
    onChange,
    onNext,
    onSkip,
}: {
    value: string
    onChange: (value: string) => void
    onNext: () => void
    onSkip: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.aiTone.title')}</h2>

                <div className="flex flex-col space-y-2 sm:space-y-4">
                    <ToneOption
                        icon={<Zap className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />}
                        label={t('onboarding.aiTone.options.directPractical')}
                        description={t('onboarding.aiTone.options.directPracticalDescription')}
                        selected={value === AI_TONE_PREFERENCES_EN.DIRECT_PRACTICAL}
                        onClick={() => onChange(AI_TONE_PREFERENCES_EN.DIRECT_PRACTICAL)}
                    />
                    <ToneOption
                        icon={<Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />}
                        label={t('onboarding.aiTone.options.gentleIntuitive')}
                        description={t('onboarding.aiTone.options.gentleIntuitiveDescription')}
                        selected={value === AI_TONE_PREFERENCES_EN.GENTLE_INTUITIVE}
                        onClick={() => onChange(AI_TONE_PREFERENCES_EN.GENTLE_INTUITIVE)}
                    />
                    <ToneOption
                        icon={<Star className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />}
                        label={t('onboarding.aiTone.options.deepSymbolic')}
                        description={t('onboarding.aiTone.options.deepSymbolicDescription')}
                        selected={value === AI_TONE_PREFERENCES_EN.DEEP_SYMBOLIC}
                        onClick={() => onChange(AI_TONE_PREFERENCES_EN.DEEP_SYMBOLIC)}
                    />
                </div>

                <p className="text-center text-xs sm:text-sm text-gray-500 px-2">{t('onboarding.aiTone.changeTime')}</p>
            </div>

            <div className="mt-auto pt-4 flex flex-col gap-3 sm:gap-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>

                <SkipButton onClick={onSkip}>
                    {t('onboarding.common.skipForNow')}
                </SkipButton>
            </div>
        </div>
    )
}

function ToneOption({
    icon,
    label,
    description,
    selected,
    onClick,
}: {
    icon: React.ReactNode
    label: string
    description: string
    selected: boolean
    onClick: () => void
}) {
    return (
        <div
            className={`flex items-center p-2.5 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${selected
                ? "border-theme-dark bg-[#E8E9EF]"
                : "border-gray-200 hover:border-theme-dark hover:bg-[#F0F1F5]"
                }`}
            onClick={onClick}
        >
            <div className="mr-2 sm:mr-4 flex-shrink-0">{icon}</div>
            <div>
                <h3 className="font-medium text-base sm:text-lg text-theme-dark">{label}</h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-tight">{description}</p>
            </div>
        </div>
    )
}

// Credit Intro Step
export function CreditIntroStep({
    value,
    onChange,
    onNext,
}: {
    value: boolean
    onChange: (value: boolean) => void
    onNext: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.creditIntro.title')}</h2>

                <div className="bg-[#E8E9EF] p-4 sm:p-6 rounded-lg border border-theme-dark">
                    <div className="flex justify-center mb-3 sm:mb-4">
                        <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-theme-dark flex items-center justify-center">
                            <Sparkles className="h-6 w-6 sm:h-8 sm:w-8  text-[#3d405b]" />
                        </div>
                    </div>

                    <h3 className="text-base sm:text-lg font-medium text-center mb-2">{t('onboarding.creditIntro.startWithFreeCredits')}</h3>

                    <p className="text-center text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">{t('onboarding.creditIntro.eachReadingUsesOneCredit')}</p>

                    <div className="flex justify-center  text-[#3d405b]">
                        <div className="flex space-x-2">
                            <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[#3d405b] flex items-center justify-center">
                                <span className="text-white text-sm sm:text-base font-medium">1</span>
                            </div>
                            <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full  bg-[#3d405b]  flex items-center justify-center">
                                <span className="text-white text-sm sm:text-base font-medium">2</span>
                            </div>
                            <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[#3d405b] flex items-center justify-center">
                                <span className="text-white text-sm sm:text-base font-medium">3</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-start space-x-2">
                    <Checkbox
                        id="credit-ack"
                        checked={value}
                        onCheckedChange={(checked) => onChange(checked as boolean)}
                        className="w-6 h-6 sm:w-7 sm:h-7 border-2 text-white   mt-0.5"
                    />
                    <Label htmlFor="credit-ack" className="text-gray-700 text-sm sm:text-lg leading-tight pt-1">{t('onboarding.creditIntro.understandCredits')}</Label>
                </div>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>
            </div>
        </div>
    )
}

// Privacy Step
export function PrivacyStep({
    value,
    onChange,
    onNext,
}: {
    value: boolean
    onChange: (value: boolean) => void
    onNext: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.privacy.title')}</h2>

                <div className="flex justify-center mb-3 sm:mb-4">
                    <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-[#E8E9EF] flex items-center justify-center">
                        <Lock className="h-6 w-6 sm:h-8 sm:w-8 text-theme-dark" />
                    </div>
                </div>

                <p className="text-center text-sm sm:text-base text-gray-700 px-2">{t('onboarding.privacy.everythingPrivate')}</p>

                <div className="flex items-start space-x-2">
                    <Checkbox
                        id="privacy-consent"
                        checked={value}
                        onCheckedChange={(checked) => onChange(checked as boolean)}
                        className="w-6 h-6 sm:w-7 sm:h-7 border-2 text-white mt-0.5"
                    />
                    <Label htmlFor="privacy-consent" className="text-gray-700 text-sm sm:text-lg leading-tight pt-1">{t('onboarding.privacy.understandPrivateReadings')}</Label>
                </div>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onNext} disabled={!value}>
                    {t('onboarding.common.continue')}
                </StepButton>
            </div>
        </div>
    )
}

// Completion Step
export function CompletionStep({
    onSubmit,
}: {
    onSubmit: () => void
}) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col flex-1">
            <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-serif text-center text-[#3d405b] px-2">{t('onboarding.completion.title')}</h2>

                <div className="flex justify-center">
                    <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-[#E8E9EF] flex items-center justify-center">
                        <Sparkles className="h-10 w-10 sm:h-12 sm:w-12 text-theme-dark" />
                    </div>
                </div>

                <p className="text-center text-sm sm:text-base text-gray-700 px-2">{t('onboarding.completion.allSet')}</p>
            </div>

            <div className="mt-auto pt-4">
                <StepButton onClick={onSubmit}>{t('onboarding.completion.beginReading')}</StepButton>
            </div>
        </div>
    )
}
